import { Store } from '@ngxs/store';
import { ViewState } from './state/view.state';
import { ViewBreakpoint } from './models/view-breakpoint.enum';
import { ViewLayoutStatus } from './models/view-layout-status.enum';
import { ViewLayout } from './models/view-layout.enum';
import { StoryLabelsState } from '../story/state/labels/story.labels.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { get, trim } from 'lodash';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
var LayoutService = /** @class */ (function () {
    function LayoutService(store) {
        this.store = store;
        this.layout = null;
        this.breakpoint = null;
    }
    LayoutService.prototype.getBase = function () {
        return {
            layout: this.store.selectSnapshot(ViewState.getLayout),
            breakpoint: this.store.selectSnapshot(ViewState.getBreakpoint),
            loaderActive: this.store.selectSnapshot(ViewState.getLoaderActive)
        };
    };
    LayoutService.prototype.isDrawerVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Large breakpoint, never show
        if (breakpoint === ViewBreakpoint.Large) {
            return false;
        }
        // Show for loft & studio
        if (environment.application !== EnvironmentApplication.Viewer) {
            return true;
        }
        // Default layout (description)
        var defaultDrawerVisible = layout.drawer.enable && this.doesContentShow();
        // Medium & Small breakpoint
        // Show drawer when enabled
        // Show drawer when disabled, but tools/info modes active
        var visible = defaultDrawerVisible
            || layout.tools.status === ViewLayoutStatus.Show
            || layout.nodeInfo.status === ViewLayoutStatus.Show
            || layout.labelInfo.status === ViewLayoutStatus.Show;
        return visible;
    };
    LayoutService.prototype.isColumnVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Large breakpoint, never show
        if (breakpoint !== ViewBreakpoint.Large) {
            return false;
        }
        // Hide on collapse
        if (layout.column.status === ViewLayoutStatus.Collapse) {
            return false;
        }
        // Always show for loft & studio
        if (environment.application !== EnvironmentApplication.Viewer) {
            return true;
        }
        // Default layout (description)
        return layout.column.enable && this.doesContentShow();
    };
    LayoutService.prototype.isColumnToggleVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Large breakpoint, never show
        if (breakpoint !== ViewBreakpoint.Large) {
            return false;
        }
        return layout.column.enable && this.doesContentShow();
    };
    LayoutService.prototype.isToolsVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        return this.isLayoutVisible(ViewLayout.Tools);
    };
    LayoutService.prototype.isToolbarVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        return (breakpoint === ViewBreakpoint.Large) && layout.menu.enable;
    };
    LayoutService.prototype.isNavbarVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Navbar is part of toolbar at large, never show
        if (breakpoint === ViewBreakpoint.Large) {
            return false;
        }
        return layout.pagination.enable || layout.media.enable;
    };
    LayoutService.prototype.isLogoVisible = function () {
        var layout = this.getBase().layout;
        return layout.logo.enable;
    };
    LayoutService.prototype.isCameraVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        return layout.camera.enable;
    };
    LayoutService.prototype.isPaginationVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, breakpoint = _a.breakpoint, layout = _a.layout;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Only show pagination on large
        // On medium and small, navbar component contains pagination
        if (breakpoint !== ViewBreakpoint.Large) {
            return false;
        }
        return layout.pagination.enable;
    };
    LayoutService.prototype.isMenuVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Large breakpoint, never show
        if (breakpoint === ViewBreakpoint.Large) {
            return false;
        }
        // Media / Small, defer to enable
        return layout.menu.enable;
    };
    LayoutService.prototype.isNodeInfoColumnVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        if (breakpoint === ViewBreakpoint.Large && this.isLayoutVisible(ViewLayout.NodeInfo)) {
            return true;
        }
        // Medium / Small, defer to drawer enabled
        if (breakpoint !== ViewBreakpoint.Large) {
            return !layout.drawer.enable && this.isLayoutVisible(ViewLayout.NodeInfo);
        }
    };
    LayoutService.prototype.isNodeInfoVisible = function (location) {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        // Large, never show
        if (breakpoint === ViewBreakpoint.Large && location === 'column') {
            return this.isLayoutVisible(ViewLayout.NodeInfo);
        }
        // Medium / Small, drawer and column
        if (breakpoint !== ViewBreakpoint.Large && location === 'drawer') {
            return this.isLayoutVisible(ViewLayout.NodeInfo);
        }
        return false;
    };
    LayoutService.prototype.isSearchColumnVisible = function () {
        var _a = this.getBase(), loaderActive = _a.loaderActive, layout = _a.layout, breakpoint = _a.breakpoint;
        // Hide when loader is active
        if (loaderActive) {
            return false;
        }
        return this.isLayoutVisible(ViewLayout.Search);
    };
    LayoutService.prototype.isVisible = function (layout) {
        switch (layout) {
            case ViewLayout.Column:
                return this.isColumnVisible();
            case ViewLayout.ColumnToggle:
                return this.isColumnToggleVisible();
            case ViewLayout.Drawer:
                return this.isDrawerVisible();
            case ViewLayout.Tools:
                return this.isToolsVisible();
            case ViewLayout.Toolbar:
                return this.isToolbarVisible();
            case ViewLayout.Logo:
                return this.isLogoVisible();
            case ViewLayout.Camera:
                return this.isCameraVisible();
            case ViewLayout.Pagination:
                return this.isPaginationVisible();
            case ViewLayout.Menu:
                return this.isMenuVisible();
            case ViewLayout.Search:
                return this.isSearchColumnVisible();
            case ViewLayout.Navbar:
                return this.isNavbarVisible();
            default:
                return this.isLayoutVisible(layout);
        }
    };
    LayoutService.prototype.isLayoutVisible = function (layout) {
        var _layout = this.store.selectSnapshot(ViewState.getLayout);
        var showLoader = this.store.selectSnapshot(ViewState.getLoaderActive);
        var el = _layout[layout];
        if (!el) {
            console.warn('Attempting to check visibility of a non-existent element: ${element}');
            return false;
        }
        return !showLoader &&
            _layout[layout].enable &&
            _layout[layout].status !== ViewLayoutStatus.Hide;
    };
    LayoutService.prototype.isLayoutEnabled = function (layout) {
        var _layout = this.store.selectSnapshot(ViewState.getLayout);
        return _layout[layout].enable;
    };
    // Checks if any content will actually show in the column or drawer
    LayoutService.prototype.doesContentShow = function () {
        var layout = this.getBase().layout;
        var labels = this.store.selectSnapshot(StoryLabelsState.getChapterLabels);
        var chapterCount = this.store.selectSnapshot(StoryChaptersState.getChapterCount);
        var description = get(this.store.selectSnapshot(StoryChaptersState.getSelectedChapter), 'info.description');
        return (layout.labelList.enable && labels.length > 0) ||
            (layout.chapterList.enable && chapterCount > 1) ||
            (description != null && trim(description).length > 0);
    };
    LayoutService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function LayoutService_Factory() { return new LayoutService(i0.ɵɵinject(i1.Store)); }, token: LayoutService, providedIn: "root" });
    return LayoutService;
}());
export { LayoutService };
